import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DeleteFileType,
  TIME_PROJECT_DETAIL,
} from "../../../../src/domains/projects-list/type";
import ProjectsRepository, { BimType } from "./projects.repository";
import { QUERY_OPTIONS } from "../../../../src/domains/shared/constants/constants";
import { queryClient } from "../../../../src/pages/_app";
import notice from "../../../../src/domains/shared/utils/notice";
import {
  responseStatusOf,
  shouldRetryWriteRequest,
} from "../utils/requestFailure";

import { DxfMultiLayer } from "../../../../src/store/dxfGraphStore";
import getPointPositionFromgetImageKey from "../../../../src/domains/shared/api/getPointPositionFromgetImageKey";
import { NearImageResponseType } from "../../../../src/domains/shared/components/PicAnnotationImage/type";
import { Response } from "../../../../src/domains/projects-list/type";
import { ProjectDetailId } from "../../../../src/domains/timeseries/TimeSeriesPage.model";
import {
  EtcDataType,
  LayerDataGroups,
  normalizeLayerDataStatus,
} from "./projects.models";
import { useTranslation } from "react-i18next";
import { mappingRequestFailureMessageKey } from "src/domains/projects-list/utils/autoBuildRejection";
import {
  renameLayerDisplayName,
  RenameLayerParams,
} from "../../../../src/domains/shared/api/renameLayerDisplayName";
import { getDrawingMeshes } from "../../../../src/domains/shared/api/drawingMesh/getDrawingMeshes";
import { renameDrawingMesh } from "../../../../src/domains/shared/api/drawingMesh/renameDrawingMesh";
import { renameDrawingBundle } from "../../../../src/domains/shared/api/drawingMesh/renameDrawingBundle";
import {
  uploadDwg,
  UploadDwgParams,
} from "src/domains/projects-list/api/drawingMesh/uploadDwg";
import {
  uploadDrawingAlignment,
  UploadDrawingAlignmentParams,
} from "src/domains/projects-list/api/drawingMesh/uploadDrawingAlignment";
import {
  uploadDrawingBundle,
  UploadDrawingBundleParams,
} from "src/domains/projects-list/api/drawingMesh/uploadDrawingBundle";
import { deleteDwg } from "src/domains/projects-list/api/drawingMesh/deleteDwg";
import { deleteDrawingBundle } from "src/domains/projects-list/api/drawingMesh/deleteDrawingBundle";
import {
  uploadLayerData,
  UploadLayerDataParams,
} from "src/domains/projects-list/api/layerData/uploadLayerData";
import {
  deleteLayerData,
  LayerChannel,
} from "src/domains/shared/api/deleteLayerData";
import {
  layerDataKey,
  layerOptions2dProjectKey,
} from "src/domains/shared/api/layerQueryKeys";
import {
  renameRejectionKey,
  RenameRejectionKey,
} from "src/domains/shared/api/renameRejection";
import {
  DrawingMeshCatalogResponse,
  RenameDrawingBundleParams,
  RenameDrawingMeshParams,
} from "../../../../src/domains/shared/api/drawingMesh/drawingMesh.types";

export interface UseGetProjectsProps {
  isOwn?: boolean;
}

/**
 * 프로젝트 목록 쿼리 키. 접두 `PROJECTS_LIST_QUERY_KEY` 로 무효화·재조회하면 `isOwn` 변형(소유·전체)이 함께 다시
 * 읽힌다 — 소유권 이전·회차 매핑처럼 목록이 통째로 바뀌는 쓰기가 쓴다.
 */
export const PROJECTS_LIST_QUERY_KEY = ["getProjectsList"] as const;
export const projectsListQueryKey = (isOwn: boolean | undefined) =>
  [...PROJECTS_LIST_QUERY_KEY, isOwn] as const;

/**
 * 소속(또는 소유) 프로젝트 목록. 봉투 실패는 리포지토리 경계에서 오류로 승격되므로
 * `data` 는 성공했을 때의 `ProjectType[]` 뿐이고, 실패는 `isError` 로만 나타난다.
 *
 * 호출부가 `placeholderData` 를 주입하던 오버라이드 구멍은 제거했다 — 성공·실패 봉투를
 * 흉내 내는 placeholder 는 소비처가 위조본과 실제 결과를 구별할 수 없게 만들고,
 * 실제 효과는 첫 렌더부터 `isLoading` 을 false 로 만들어 로딩 표시를 죽이는 것이었다.
 */
export const useGetProjects = (args?: UseGetProjectsProps | undefined) => {
  const isOwn = args?.isOwn;
  return useQuery(
    projectsListQueryKey(isOwn),
    () => ProjectsRepository.getProjectsList({ isOwn }),
    {
      ...QUERY_OPTIONS,
      refetchOnWindowFocus: true,
    }
  );
};

export const useGetProjectByID = (id: number | undefined) => {
  return useQuery(
    ["getProjectById", id],
    async () => {
      if (!id) throw new Error("No ID provided");
      return ProjectsRepository.getProjectById(id);
    },
    {
      ...QUERY_OPTIONS,
      enabled: !!id,
      keepPreviousData: true,
    }
  );
};

/**
 * 프로젝트 시계열 목록의 조회 키 — 조회 밖에서 그 캐시를 읽는 자리(이슈 쓰기의 mesh 확인)도 이 키를 쓴다. 단건 키
 * (`timeSeriesDetailKey`)의 접두이기도 해서, 이 키로 무효화하면 그 프로젝트의 단건 시계열도 함께 무효화된다.
 */
export const timeSeriesListKey = (projectId: number | undefined) =>
  ["getTimeseriesById", projectId] as const;

/**
 * 단건 시계열의 조회 키. 매핑 요청 뒤의 갱신(`usePostMappingRequest`)은 목록 키 무효화가 이 키까지 닿는 데 기대므로, 접두를
 * 목록 키와 가르면 패널의 요청 상태가 묵는다.
 */
const timeSeriesDetailKey = (projectId: number, timeSeriesId: number) =>
  [...timeSeriesListKey(projectId), timeSeriesId] as const;

export const useGetTimeSeries = (projectId: number | undefined) => {
  return useQuery(
    timeSeriesListKey(projectId),
    () => ProjectsRepository.getTimeSeries(projectId!),
    { ...QUERY_OPTIONS, enabled: !!projectId }
  );
};

const selectCompletedTimeSeriesMap = (
  res: Awaited<ReturnType<typeof ProjectsRepository.getTimeSeries>>
) => {
  if (res.status !== "Success") {
    return {};
  }

  return res.data
    .filter((i) => i.current_state === "completed")
    .reduce<Record<number, string>>((acc, i) => {
      acc[i.id] = i.timestamp;
      return acc;
    }, {});
};

export const useCompletedTimeSeriesMap = (projectId: number) => {
  return useQuery(
    ["getTimeseriesById", projectId],
    () => ProjectsRepository.getTimeSeries(projectId),
    {
      ...QUERY_OPTIONS,
      enabled: !!projectId,
      select: selectCompletedTimeSeriesMap,
    }
  );
};

interface TimeSeriesByIdOptions {
  /** react-query `refetchInterval` 그대로 — 생략하면 주기적으로 다시 읽지 않는다. */
  refetchInterval?:
    | number
    | false
    | ((data: Response<TIME_PROJECT_DETAIL> | undefined) => number | false);
}

/** 단건 시계열 — 주기를 넘기는 소비처는 매핑 정보 패널뿐이다(mapping-request 「자동 처리 중의 갱신」). */
export const useGetTimeSeriesById = (
  { projectId, timeSeriesId }: ProjectDetailId,
  { refetchInterval }: TimeSeriesByIdOptions = {}
) => {
  return useQuery(
    timeSeriesDetailKey(projectId, timeSeriesId),
    () => ProjectsRepository.getTimeSeriesById(projectId!, timeSeriesId!),
    {
      ...QUERY_OPTIONS,
      enabled: !!projectId && !!timeSeriesId,
      refetchInterval,
    }
  );
};

export function useCheck3dDataExists(projectId: number | undefined) {
  return useQuery(
    ["check3dDataExists", projectId],
    () => ProjectsRepository.check3dDataExists(projectId!),
    {
      retry: 2,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: Infinity,
      enabled: Boolean(projectId),
    }
  );
}

export const useProjectMeta = ({
  projectId,
  timeSeriesId,
}: {
  projectId?: number;
  timeSeriesId?: number;
}) => {
  return useQuery(
    ["projectMeta", projectId, timeSeriesId],
    () =>
      ProjectsRepository.getProjectMeta({
        projectId: projectId!,
        timeSeriesId: timeSeriesId!,
      }),
    {
      retry: false,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retryOnMount: false,
      enabled: !!projectId && !!timeSeriesId,
    }
  );
};

export const usePatchProjectByID = () => {
  return useMutation({
    mutationKey: ["patchProjectByID"],
    mutationFn: (data: any) => ProjectsRepository.patchProjectByID(data),
    retry: 3,
    retryDelay: 1000,
  });
};

export const useDeleteProjectTimeseriesByID = () => {
  return useMutation({
    mutationKey: ["deleteProjectByID"],
    mutationFn: ProjectsRepository.deleteProjectTimeseriesByID,
    ...DELETE_MUTATION_RETRY,
  });
};

export const useGetS3Imgs = (
  id: number | undefined,
  type: "original" | "compressed" | "thumbnail"
) => {
  return useQuery(
    ["getS3Imgs", Number(id), type],
    () => ProjectsRepository.getS3Imgs(id!, type),
    { ...QUERY_OPTIONS, enabled: !!id }
  );
};

export const useGetS3ImgsWithMetadata = (
  id: number | undefined,
  type: "original" | "compressed" | "thumbnail"
) => {
  return useQuery(
    ["getS3ImgsWithMetadata", Number(id), type],
    () => ProjectsRepository.getS3ImgsWithMetadata(id!, type),
    { ...QUERY_OPTIONS, enabled: !!id }
  );
};

/**
 * 회차 드론 이미지의 **업로드 원본** 크기 — 이미지 목록(썸네일 listing)과 **따로 둔 조회**다. 그래서 이 조회의
 * 성패는 목록의 렌더 갈래를 바꾸지 않고, 그 렌더 지점은 목록 푸터의 「총 용량」 칸 하나다
 * (spec: project-drone-image-management).
 */
export const useGetOriginalDroneImageSizes = (id: number | undefined) => {
  return useQuery(
    ["getOriginalDroneImageSizes", Number(id)],
    () => ProjectsRepository.getOriginalDroneImageSizes(id!),
    { ...QUERY_OPTIONS, enabled: !!id }
  );
};

export type GetMappingImages = {
  projectId: number | undefined;
  timeSeriesId: number | undefined;
  lng: number;
  lat: number;
  filter: "nearby" | "all";
};

export const useGetMappingImages = ({
  projectId,
  timeSeriesId,
  lng = 0,
  lat = 0,
  filter = "nearby",
  enabled = true,
}: GetMappingImages & { enabled?: boolean }) => {
  return useQuery<NearImageResponseType>(
    ["getMappingImgs", projectId, timeSeriesId, lng, lat, filter],
    () =>
      ProjectsRepository.getMappingImages({
        projectId,
        timeSeriesId,
        lng,
        lat,
        filter,
      }),
    {
      ...QUERY_OPTIONS,
      retry: false,
      enabled: enabled && !!projectId && !!timeSeriesId && !!lng && !!lat,
    }
  );
};

export const useGetPositionFromImageName = ({
  projectId,
  timeSeriesId,
  imageId,
}: {
  projectId: number;
  timeSeriesId: number;
  imageId: number | undefined;
}) => {
  return useQuery(
    ["getPositionImgName", projectId, timeSeriesId, imageId],
    () => getPointPositionFromgetImageKey({ projectId, timeSeriesId, imageId }),
    {
      ...QUERY_OPTIONS,
      enabled: !!projectId || !!timeSeriesId || !!imageId,
    }
  );
};

export const usePostS3ImgsProcess = (
  id: number | undefined,
  type: "original" | "compressed" | "thumbnail",
  isSelected: boolean
) => {
  return useQuery(
    ["postS3ImgsProcess", Number(id), type],
    () => ProjectsRepository.postS3ImgsProcess(id, type),
    { refetchInterval: 1000, enabled: isSelected && !!id }
  );
};

export const useGetFavoriteDroneImageList = (
  projectId: number | undefined,
  timeSeriesId: number | undefined
) => {
  return useQuery(
    ["getFavoriteDroneImageList", Number(projectId), Number(timeSeriesId)],
    () =>
      ProjectsRepository.getFavoriteDroneImageList(projectId!, timeSeriesId!),
    { ...QUERY_OPTIONS, enabled: !!projectId && !!timeSeriesId }
  );
};

export const usePatchFavoriteDroneImageList = () => {
  return useMutation({
    mutationKey: ["patchFavoriteDroneImageList"],
    mutationFn: (arg: {
      projectId: number;
      timeSeriesId: number;
      imageName: string;
    }) => ProjectsRepository.patchFavoriteDroneImageList(arg),
    onSettled: (_, __, data) => {
      queryClient.refetchQueries({
        queryKey: [
          "getFavoriteDroneImageList",
          Number(data.projectId),
          Number(data.timeSeriesId),
        ],
      });
    },
    retry: 3,
    retryDelay: 1000,
  });
};

const S3_ETC_DATA_QUERY_ID = "getS3Etcdata";

/**
 * 참고 데이터(S3 기타 파일) 쿼리 키. `type` 을 생략하면 프로젝트 전체를 가리키는
 * 접두 키가 되어 세 종류를 한 번에 무효화할 수 있다.
 * 키 문자열은 `S3_ETC_DATA_QUERY_ID` 한 곳에서만 정의되며, 캐시를 읽는 `useGetS3EtcData` 와
 * 무효화하는 `invalidateProjectStorageUsage`·`useEtcDataUploader` 가 모두 이 팩토리를 거친다.
 */
export const s3EtcDataKey = (projectId: number, type?: EtcDataType) =>
  type === undefined
    ? ([S3_ETC_DATA_QUERY_ID, Number(projectId)] as const)
    : ([S3_ETC_DATA_QUERY_ID, Number(projectId), type] as const);

export const useGetS3EtcData = (id: number | undefined, type: EtcDataType) => {
  return useQuery(
    s3EtcDataKey(Number(id), type),
    () => ProjectsRepository.getS3EtcData(id!, type),
    { ...QUERY_OPTIONS, enabled: !!id }
  );
};

export { layerDataKey };

const hasProcessingLayerData = (data?: LayerDataGroups) =>
  !!data &&
  Object.values(data).some((group: LayerDataGroups[keyof LayerDataGroups]) =>
    group.some((item) => {
      const status = normalizeLayerDataStatus(item.status);
      return status === "before" || status === "on_processing";
    })
  );

export const useGetLayerData = (projectId: number | undefined) => {
  return useQuery(
    layerDataKey(projectId!),
    () => ProjectsRepository.getLayerData(projectId!),
    {
      ...QUERY_OPTIONS,
      // 처리 상태를 담고 있으므로 기본 10분 stale 은 부적합
      staleTime: 0,
      enabled: !!projectId,
      refetchOnWindowFocus: "always",
      // 처리 미완료 항목이 있는 동안만 GET 자체를 폴링 — 파일별 상태 폴링을 대체한다
      refetchInterval: (data) => (hasProcessingLayerData(data) ? 5000 : false),
    }
  );
};

/**
 * 레이어 데이터 목록을 **한 번** 읽는다 — `useGetLayerData` 와 같은 키·같은 요청이라 캐시를 나눠 쓴다.
 *
 * 처리 상태를 따라가지 않는 소비처(토공 탭 진입 판정)용이다. `useGetLayerData` 는 LayerData 탭의 상태 표시를 위해
 * 포커스마다 다시 읽고 처리 중에는 5초마다 폴링하는데, 목록을 그리지 않는 소비처가 그 옵저버를 달면 쓸 곳 없는 재조회가
 * 그 화면까지 따라온다. LayerData 탭이 함께 떠 있으면 그쪽 갱신이 같은 캐시로 여기에도 닿는다.
 */
export const useGetLayerDataSnapshot = (
  projectId: number | undefined,
  enabled: boolean
) => {
  return useQuery(
    layerDataKey(projectId!),
    () => ProjectsRepository.getLayerData(projectId!),
    { ...QUERY_OPTIONS, enabled: !!projectId && enabled }
  );
};

/**
 * rename 거부 범주 → `projects` 네임스페이스의 `layerRename.*` 문구 키. 이 파일의 세 rename mutation
 * (`useRenameProjectLayer`·`useRenameDrawingMesh`·`useRenameDrawingBundle`)이 onError 에서
 * `renameErrorMessageKey` 로 읽는다. 에러에서 상태 코드를 꺼내는 식은 호출 경로마다 달라(Amplify 게이트웨이
 * `response.status ?? response.statusCode` · raw fetch 에러의 `status`) 각 onError 에 남는다.
 *
 * 상태 코드 → 범주 표는 `renameRejectionKey`(`shared/api/renameRejection`)에 있고, timeseries 의
 * `useRename2dLayer`·`useRename3dLayer`(`layerRenameErrorMessage`)도 같은 표에서 범주를 고른다 — 상태 코드의
 * 뜻이 바뀌면 이 파일의 세 rename 과 두 패널의 안내가 함께 바뀐다. 여기서 갈리는 것은 문구 자원뿐이다.
 */
const RENAME_ERROR_MESSAGE_KEY = {
  renameDuplicate: "layerRename.renameDuplicate",
  renameInvalid: "layerRename.renameInvalid",
  renameNoPermission: "layerRename.renameNoPermission",
  renameFailed: "layerRename.renameFailed",
} as const satisfies Record<RenameRejectionKey, string>;

/** 표에 없는 상태(상태 코드가 없는 네트워크 실패 포함)는 `renameFailed` 범주라 `layerRename.renameFailed` 다. */
const renameErrorMessageKey = (status: unknown) =>
  RENAME_ERROR_MESSAGE_KEY[renameRejectionKey(status)];

export const useRenameProjectLayer = (projectId: number) => {
  const cacheKey = layerDataKey(projectId);
  const { t } = useTranslation("projects");

  return useMutation({
    mutationFn: (params: RenameLayerParams) =>
      renameLayerDisplayName(projectId, params),
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: cacheKey });
      const previousData = queryClient.getQueryData<LayerDataGroups>(cacheKey);
      // 캐시는 API 그룹 키(linear 포함)를 그대로 보존하므로 params.type 으로 직접 인덱싱한다
      queryClient.setQueryData<LayerDataGroups>(cacheKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          [params.type]: old[params.type].map((item) =>
            item.origin_file_name === params.origin_file_name
              ? { ...item, display_name: params.display_name }
              : item
          ),
        };
      });
      return { previousData };
    },
    onError: (err, _params, context) => {
      if (context?.previousData)
        queryClient.setQueryData(cacheKey, context.previousData);
      queryClient.invalidateQueries({ queryKey: cacheKey });
      const status =
        (err as any)?.response?.status ?? (err as any)?.response?.statusCode;
      notice("error", t(renameErrorMessageKey(status), { ns: "projects" }));
    },
    onSuccess: () => {
      // rename 은 프로젝트 전역 — 2D 검사 패널 레이어 목록 캐시도 동기화한다
      queryClient.invalidateQueries({
        queryKey: layerOptions2dProjectKey(projectId),
      });
    },
  });
};

export const drawingMeshesKey = (projectId: number) =>
  ["drawingMeshes", Number(projectId)] as const;

// 설계도 mesh 카탈로그 — 설계도 박스·횡단면 그래프/반출/수량표 등 여러 소비처가 공유한다.
// 호출 측에서 자신이 활성화돼야 할 조건일 때만 enabled=true 를 넘긴다.
export const useGetDrawingMeshes = (projectId: number, enabled: boolean) => {
  return useQuery(
    drawingMeshesKey(projectId),
    () => getDrawingMeshes(projectId),
    { ...QUERY_OPTIONS, enabled: !!projectId && enabled }
  );
};

export const useRenameDrawingMesh = (projectId: number) => {
  const cacheKey = drawingMeshesKey(projectId);
  const { t } = useTranslation("projects");

  return useMutation({
    mutationFn: (params: RenameDrawingMeshParams) =>
      renameDrawingMesh(projectId, params),
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: cacheKey });
      const previousData =
        queryClient.getQueryData<DrawingMeshCatalogResponse>(cacheKey);
      queryClient.setQueryData<DrawingMeshCatalogResponse>(cacheKey, (old) => {
        if (!old) return old;
        // 형제 축(`bundles`·`groups`)을 **펼쳐서** 잇는다. 새 객체를 `drawings` 하나로 세우면
        // 둘이 조용히 사라지고(둘 다 optional 이라 타입이 잡아 주지 않는다) 다음 재조회까지
        // 계층이 무너진다 — 묶음 행과 폴더 행이 사라지고, 아직 안 지어진 구성원이 평면 행으로
        // 드러난다. 이름 하나를 바꾼 대가로 목록이 다른 화면이 되어서는 안 된다.
        return {
          ...old,
          drawings: old.drawings.map((item) =>
            item.name === params.name
              ? { ...item, display_name: params.display_name }
              : item
          ),
        };
      });
      return { previousData };
    },
    onError: (err, _params, context) => {
      if (context?.previousData)
        queryClient.setQueryData(cacheKey, context.previousData);
      queryClient.invalidateQueries({ queryKey: cacheKey });
      const status = (err as any)?.status;
      notice("error", t(renameErrorMessageKey(status), { ns: "projects" }));
    },
  });
};

// 설계도(dwg) 업로드 — 서버 경유 main-api(S3 직접 Storage.put 대체). 실패 notice 는 호출 측
// (useStratumLayerUploader)이 기존 업로드 실패 문구를 재사용한다.
/**
 * 묶음 표시명 변경 — `useRenameDrawingMesh` 의 거울이다. 낙관 갱신은 `bundles[]` 에서 `name` 이 일치하는 원소의
 * `display_name` 만 바꾸고(`null` 은 reset 이라 `name`), 형제 축(`drawings`·`groups`)은 펼쳐서 잇는다 — 도면 쪽
 * 주석의 그 사고다. 에러 문구는 도면과 같은 표(`renameErrorMessageKey`)에서 고른다: 409(같은 프로젝트의 다른 묶음이
 * 쓰는 표시명 — 도면 표시명과는 서로를 막지 않는다)·400·403/404(비멤버·없는 묶음)의 뜻이 도면과 같다.
 *
 * rename 은 구성원을 움직이지 않는다 — 도면 항목의 `bundle_name`·`display_name` 은 손대지 않는다.
 */
export const useRenameDrawingBundle = (projectId: number) => {
  const cacheKey = drawingMeshesKey(projectId);
  const { t } = useTranslation("projects");

  return useMutation({
    mutationFn: (params: RenameDrawingBundleParams) =>
      renameDrawingBundle(projectId, params),
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: cacheKey });
      const previousData =
        queryClient.getQueryData<DrawingMeshCatalogResponse>(cacheKey);
      queryClient.setQueryData<DrawingMeshCatalogResponse>(cacheKey, (old) => {
        if (!old?.bundles) return old;
        return {
          ...old,
          bundles: old.bundles.map((bundle) =>
            bundle.name === params.name
              ? { ...bundle, display_name: params.display_name ?? bundle.name }
              : bundle
          ),
        };
      });
      return { previousData };
    },
    onError: (err, _params, context) => {
      if (context?.previousData)
        queryClient.setQueryData(cacheKey, context.previousData);
      queryClient.invalidateQueries({ queryKey: cacheKey });
      const status = (err as any)?.status;
      notice("error", t(renameErrorMessageKey(status), { ns: "projects" }));
    },
  });
};

export const useUploadDwg = (projectId: number) => {
  return useMutation({
    mutationKey: ["uploadDwg", Number(projectId)],
    mutationFn: (params: UploadDwgParams) => uploadDwg(projectId, params),
  });
};

// 도면 정렬(선형) dxf 업로드 — dwg 다리가 앵커를 확정한 뒤 그 도면의 슬롯으로 나가는 두 번째
// 다리다. 실패 notice 는 호출 측(useStratumLayerUploader)이 부분 실패 문면과 함께 낸다 — 「도면은
// 올라갔고 정렬만 비었다」는 일반 업로드 실패와 다른 사건이라 여기서 뭉뚱그리지 않는다.
export const useUploadDrawingAlignment = (projectId: number) => {
  return useMutation({
    mutationKey: ["uploadDrawingAlignment", Number(projectId)],
    mutationFn: (params: UploadDrawingAlignmentParams) =>
      uploadDrawingAlignment(projectId, params),
  });
};

// 도면 묶음(zip) 업로드 — dwg n장(+정렬 dxf n장)을 zip 하나로 올리면 서버가 마지막 청크에서 열어
// 도면 행 n개를 만든다. 실패 notice 는 호출 측(useStratumLayerUploader)이 서버 사유와 함께 낸다.
export const useUploadDrawingBundle = (projectId: number) => {
  return useMutation({
    mutationKey: ["uploadDrawingBundle", Number(projectId)],
    mutationFn: (params: UploadDrawingBundleParams) =>
      uploadDrawingBundle(projectId, params),
  });
};

/**
 * 삭제 mutation 재시도 규약 — `useDeleteProjectTimeseriesByID`·`useDeleteDwg`·`useDeleteDrawingBundle`·
 * `useDeleteLayerData`·`useDeleteEtcData`·`useDeleteS3Data` 여섯이 펼쳐 쓰므로 값을 바꾸면 여섯이 함께 바뀐다.
 * 이 파일의 나머지 삭제 mutation 은 묶이지 않았다: `useDeleteIssueCategory` 는 다른 값(2회·500ms)이며,
 * `useDeleteS3Imgs`·`useDeleteAllOriginalImages` 는 재시도하지 않는다.
 */
const DELETE_MUTATION_RETRY = { retry: 3, retryDelay: 1000 } as const;

// 설계도(dwg) 삭제 — 서버 경유 main-api(S3 직접 Storage.remove 대체). 도면 1건 단위 호출.
// retry 는 `DELETE_MUTATION_RETRY` 를 따르고, notice 는 호출 측
// (useStratumLayerDeletion)이 기존 삭제 성공/실패 문구를 재사용한다.
export const useDeleteDwg = (projectId: number) => {
  return useMutation({
    mutationKey: ["deleteDwg", Number(projectId)],
    mutationFn: (filename: string) => deleteDwg(projectId, filename),
    ...DELETE_MUTATION_RETRY,
  });
};

// 묶음 완전 삭제 — 그 zip 이 만든 도면 전부와 묶음 자체를 한 요청으로. retry 는 `DELETE_MUTATION_RETRY`.
export const useDeleteDrawingBundle = (projectId: number) => {
  return useMutation({
    mutationKey: ["deleteDrawingBundle", Number(projectId)],
    mutationFn: (name: string) => deleteDrawingBundle(projectId, name),
    ...DELETE_MUTATION_RETRY,
  });
};

// 레이어 채널 업로드 — 서버 경유 main-api 청크 업로드(S3 직접 Storage.put 대체). 함수 내부에
// 청크 단위 재시도가 있으므로 mutation 레벨 retry 를 겹치지 않는다(중복 재전송 방지). 실패
// notice 는 호출 측(useLayerDataUploader)이 기존 업로드 실패 문구를 재사용한다.
export const useUploadLayerData = (projectId: number) => {
  return useMutation({
    mutationKey: ["uploadLayerData", Number(projectId)],
    mutationFn: (params: UploadLayerDataParams) =>
      uploadLayerData(projectId, params),
  });
};

// 레이어 채널 삭제 — 서버 경유 main-api(S3 직접 삭제 대체). 레이어 이름 1건 단위 호출(404 는 성공).
// retry 는 `DELETE_MUTATION_RETRY` 를 따르고, notice 는 호출 측
// (useLayerDataDeletion)이 기존 삭제 성공/실패 문구를 재사용한다.
export const useDeleteLayerData = (projectId: number) => {
  return useMutation({
    mutationKey: ["deleteLayerData", Number(projectId)],
    mutationFn: ({
      channel,
      layerName,
    }: {
      channel: LayerChannel;
      layerName: string;
    }) => deleteLayerData(projectId, channel, layerName),
    ...DELETE_MUTATION_RETRY,
  });
};

export const useGetS3GcpData = (id: number | undefined) => {
  return useQuery(
    ["getGcpData", Number(id)],
    () => ProjectsRepository.getS3GcpData(id!),
    { ...QUERY_OPTIONS, enabled: !!id }
  );
};

export const useGetS3DxfMultiLayer = (
  id: number | undefined,
  fileName: string,
  type: DxfMultiLayer
) => {
  return useQuery(
    ["getDxfMultiLayer", id, fileName, type],
    () => ProjectsRepository.getS3MultiLayer(id!, fileName, type),
    { ...QUERY_OPTIONS, enabled: !!id }
  );
};

export const useGetBuiltS3BimData = (id: number, dataType: BimType) => {
  return useQuery(
    ["getBuiltS3BimData", Number(id), dataType],
    () => ProjectsRepository.getBuiltS3BimData(id, dataType),
    { ...QUERY_OPTIONS, enabled: !!id }
  );
};

export const useGetS3Cesium3DTileJsonUrl = (id: number) => {
  return useQuery(
    ["getS3Cesium3DTileJsonUrl", Number(id)],
    () => ProjectsRepository.getS3Cesium3DTileJsonUrl(id),
    { ...QUERY_OPTIONS, enabled: !!id }
  );
};

export const useDeleteEtcData = () => {
  return useMutation({
    mutationKey: ["deleteEtcData"],
    mutationFn: (files: DeleteFileType[]) =>
      ProjectsRepository.deleteEtcData(files),
    ...DELETE_MUTATION_RETRY,
  });
};

export const useDeleteS3Data = () => {
  return useMutation({
    mutationKey: ["deleteS3Data"],
    mutationFn: (files: string[]) => ProjectsRepository.deleteS3Data(files),
    ...DELETE_MUTATION_RETRY,
  });
};

export const useCreateProject = () => {
  return useMutation(["createProject"], ProjectsRepository.createProject, {
    retry: shouldRetryWriteRequest,
    retryDelay: 1000,
  });
};

export const useCreateTimeSeries = () => {
  return useMutation(
    ["createTimeseries"],
    ProjectsRepository.createTimeseries,
    {
      retry: shouldRetryWriteRequest,
      retryDelay: 1000,
    }
  );
};

export const usePatchTimeseriesById = () => {
  return useMutation(
    ["modifyTimeseries"],
    (data: {
      timeSeriesId: number;
      projectId: number;
      timestamp: string;
      comment: string;
      title?: string;
    }) => ProjectsRepository.patchTimeseriesById(data),
    {
      retry: shouldRetryWriteRequest,
      retryDelay: 1000,
    }
  );
};

export const useDeleteS3Imgs = () => {
  return useMutation({
    mutationKey: ["deleteS3Imgs"],
    mutationFn: (files: any[]) => ProjectsRepository.deleteS3Imgs(files),
  });
};

export const useDeleteAllOriginalImages = () => {
  return useMutation({
    mutationKey: ["deleteAllOriginalImages"],
    mutationFn: (projectId: number) =>
      ProjectsRepository.deleteAllOriginalImages(projectId),
  });
};

export const useGetSearchProject = (text: string) => {
  return useQuery(
    ["getSearchProjectList", text],
    () => ProjectsRepository.getSearchProjectList(text),
    // 새 검색어를 기다리는 동안 이전 검색 결과를 흐리게 보여 주려고 남긴다 — v4 는 새 키가 실패하면
    // 이전 데이터를 버리고 실패로 보고하므로 실패 갈래는 가려지지 않는다.
    { ...QUERY_OPTIONS, enabled: !!text, keepPreviousData: true }
  );
};

export const useGetIssueCategory = (id: number) => {
  return useQuery(
    ["getIssueCategory", Number(id)],
    () => ProjectsRepository.getIssueCategory(id),
    QUERY_OPTIONS
  );
};

/**
 * 이슈 유형 추가 mutation 의 키 — 이슈 유형 관리 모달의 목록(`IssueList`)이 `useIsMutating` 으로 "추가 중"을
 * 읽으므로 추가 훅과 목록이 이 상수 하나로 같은 키를 쓴다.
 */
export const CREATE_ISSUE_CATEGORY_MUTATION_KEY = [
  "createIssueCategory",
] as const;

export const useCreateIssueCategory = () => {
  return useMutation({
    mutationKey: CREATE_ISSUE_CATEGORY_MUTATION_KEY,
    mutationFn: (arg: { id: number; keyword: string }) =>
      ProjectsRepository.createIssueCategory(arg),
    retry: 3,
    retryDelay: 1000,
  });
};

export const usePatchIssueCategory = () => {
  return useMutation({
    mutationKey: ["patchIssueCategory"],
    mutationFn: (arg: { id: number; before: string; after: string }) =>
      ProjectsRepository.patchIssueCategory(arg),
    onMutate: async (data) => {
      await queryClient.cancelQueries({
        queryKey: ["getIssueCategory", Number(data.id)],
      });
      const previousIssueCategory = queryClient.getQueryData([
        "getIssueCategory",
        Number(data.id),
      ]);
      queryClient.setQueryData(
        ["getIssueCategory", Number(data.id)],
        (old: { categories: string[] } | undefined) => {
          const filteredOld = old
            ? old.categories.map((item) => {
                if (item === data.before) {
                  return data.after;
                }
                return item;
              })
            : [];
          return {
            ...old,
            categories: filteredOld,
          };
        }
      );
      return { previousIssueCategory };
    },
    onError: (err, data, context) => {
      queryClient.setQueriesData(
        ["getIssueCategory", Number(data.id)],
        context?.previousIssueCategory
      );
      notice("error", "이미 있는 이슈입니다.");
    },
    onSettled: (_, __, data) => {
      queryClient.refetchQueries({
        queryKey: ["getIssueCategory", Number(data.id)],
      });
      queryClient.invalidateQueries({
        queryKey: ["GetAnnotation", Number(data.id)],
      });
    },
    retry: 3,
    retryDelay: 1000,
  });
};

export const useDeleteIssueCategory = () => {
  return useMutation({
    mutationKey: ["deleteIssueCategory"],
    mutationFn: (arg: { id: number; keyword: string }) =>
      ProjectsRepository.deleteIssueCategory(arg),
    retry: 2,
    retryDelay: 500,
  });
};

/** 매핑 요청을 다시 보내는 최대 횟수 — 첫 시도를 더하면 최대 세 번이다. */
const MAPPING_REQUEST_NETWORK_RETRIES = 2;

/**
 * 상태 코드를 읽지 못한 실패인가 — 서버에 닿지 못한 실패만이 아니라, 닿았지만 응답을 읽지 못한 실패(CORS 헤더 없는 게이트웨이
 * 504)도 참이다. 그래서 재시도가 안전하다는 보장은 아니다.
 */
const isNetworkFailure = (error: unknown): boolean =>
  responseStatusOf(error) === undefined;

/**
 * 매핑 요청(mapping-request 「매핑 요청 결과 알림」). 요청은 멱등이 아니라서 상태 코드를 받지 못한 실패만 다시 보낸다 — 409 를
 * 다시 보내면 같은 거절만 쌓인다. 응답만 잃은 요청을 다시 보내 409 가 와도, 끝난 뒤 무효화하는 시계열 목록 키가 단건 키의
 * 접두라 단건까지 무효화돼 화면의 요청 상태를 바로잡는다.
 */
export const usePostMappingRequest = () => {
  const { t } = useTranslation("projects");
  const client = useQueryClient();
  return useMutation({
    mutationKey: ["postMappingRequest"],
    mutationFn: ({
      timeSeriesId,
      auto,
    }: {
      projectId: number;
      timeSeriesId: number;
      auto: boolean;
    }) => ProjectsRepository.postMappingRequest(timeSeriesId, { auto }),
    onSuccess: () => {
      notice("success", t("timeSeries.mappingRequestResult.succeeded"));
    },
    onError: (error) => {
      notice("error", t(mappingRequestFailureMessageKey(error)));
    },
    onSettled: (_, __, { projectId }) => {
      void client.invalidateQueries({ queryKey: timeSeriesListKey(projectId) });
      void client.invalidateQueries({ queryKey: PROJECTS_LIST_QUERY_KEY });
    },
    retry: (failureCount, error) =>
      isNetworkFailure(error) && failureCount < MAPPING_REQUEST_NETWORK_RETRIES,
    retryDelay: 1000,
  });
};

/** 자동 인식 토글 저장의 mutation 키 — 매핑 요청 모달이 `useIsMutating` 으로 「저장 중」을 읽는다. */
export const gcpAutoDetectMutationKey = (timeSeriesId: number) =>
  ["putGcpAutoDetect", Number(timeSeriesId)] as const;

/**
 * 자동 인식 토글 저장. 캐시는 서버 확정값만 품어야 모달이 저장되지 않은 토글로 `{"auto": true}` 를 보내지 않는다 — 그래서
 * 성공 응답으로만 고치고, 실패 통지는 호출 측 몫이다. 다시 읽기를 기다리지 않아야 실패한 체크 표시가 곧바로 돌아간다.
 */
export const usePutGcpAutoDetect = ({
  projectId,
  timeSeriesId,
}: {
  projectId: number;
  timeSeriesId: number;
}) => {
  const client = useQueryClient();
  const timeSeriesKey = timeSeriesDetailKey(projectId, timeSeriesId);
  return useMutation({
    mutationKey: gcpAutoDetectMutationKey(timeSeriesId),
    mutationFn: (enabled: boolean) =>
      ProjectsRepository.putGcpAutoDetect(timeSeriesId, enabled),
    onSuccess: ({ gcp_auto_detect }) => {
      client.setQueryData<Response<TIME_PROJECT_DETAIL>>(timeSeriesKey, (old) =>
        old?.status === "Success"
          ? { ...old, data: { ...old.data, gcp_auto_detect } }
          : old
      );
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: timeSeriesKey });
    },
  });
};

export const projectOrthomosaicImageKey = (
  timeSeriesId: number | undefined | null
) => ["projectOrthomosaicImage", timeSeriesId] as const;

export const useGetProjectOrthomosaicImage = (
  timeSeriesId: number | undefined | null
) => {
  return useQuery(
    projectOrthomosaicImageKey(timeSeriesId),
    () => ProjectsRepository.getProjectOrthomosaicImage(timeSeriesId!),
    {
      enabled: !!timeSeriesId,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
      cacheTime: 10 * 60 * 1000,
    }
  );
};

export const useGetTimeSeriesFilesSizes = (
  timeSeriesId: number | undefined
) => {
  return useQuery(
    ["timeSeriesFilesSizes", timeSeriesId],
    () => ProjectsRepository.getTimeSeriesFilesSizes(timeSeriesId!),
    {
      enabled: !!timeSeriesId,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
      cacheTime: 10 * 60 * 1000,
    }
  );
};

export const useGetDroneImagePreview = (
  s3Key: string | null,
  timeSeriesId: number | undefined
) => {
  return useQuery(
    ["getDroneImagePreview", timeSeriesId, s3Key],
    () => ProjectsRepository.getDroneImagePreview(s3Key),
    {
      enabled: s3Key !== null && !!timeSeriesId,
      keepPreviousData: true,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: 30 * 60 * 1000,
    }
  );
};
